'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import {
  ArrowLeft, CheckCircle, Sparkles, Music2, ChevronDown, ChevronUp, Loader2
} from 'lucide-react';

const plans = [
  {
    name: 'Starter',
    monthlyPrice: 49,
    annualPrice: 39,
    desc: 'Perfect for solo DJs just getting started',
    highlight: false,
    features: {
      'Public Website': true,
      'Live Booking Calendar': true,
      'Client Portal': true,
      'E-Signature Contracts': true,
      'Payment Gateway': true,
      'Vendor Portal': false,
      'Automated Vendor Dispatch': false,
      'AI Virtual Assistant': '100 msgs/mo',
      'Vendors in CRM': 'Up to 5',
      'Real-Time Messaging': false,
      'PDF Day-Of Brief': false,
      'White-Label Branding': 'Basic',
      'Custom Domain': false,
      'Spotify Playlist Builder': true,
      'Timeline Builder': true,
      'Priority Support': false,
      'API Access': false,
    },
  },
  {
    name: 'Professional',
    monthlyPrice: 99,
    annualPrice: 79,
    desc: 'For growing entertainment businesses',
    highlight: true,
    features: {
      'Public Website': true,
      'Live Booking Calendar': true,
      'Client Portal': true,
      'E-Signature Contracts': true,
      'Payment Gateway': true,
      'Vendor Portal': true,
      'Automated Vendor Dispatch': true,
      'AI Virtual Assistant': 'Unlimited',
      'Vendors in CRM': 'Unlimited',
      'Real-Time Messaging': true,
      'PDF Day-Of Brief': true,
      'White-Label Branding': 'Full',
      'Custom Domain': true,
      'Spotify Playlist Builder': true,
      'Timeline Builder': true,
      'Priority Support': false,
      'API Access': false,
    },
  },
  {
    name: 'Enterprise',
    monthlyPrice: 199,
    annualPrice: 159,
    desc: 'For agencies managing multiple brands',
    highlight: false,
    features: {
      'Public Website': true,
      'Live Booking Calendar': true,
      'Client Portal': true,
      'E-Signature Contracts': true,
      'Payment Gateway': true,
      'Vendor Portal': true,
      'Automated Vendor Dispatch': true,
      'AI Virtual Assistant': 'Unlimited',
      'Vendors in CRM': 'Unlimited',
      'Real-Time Messaging': true,
      'PDF Day-Of Brief': true,
      'White-Label Branding': 'Full + Multi-Brand',
      'Custom Domain': true,
      'Spotify Playlist Builder': true,
      'Timeline Builder': true,
      'Priority Support': true,
      'API Access': true,
    },
  },
];

const featureRows = Object.keys(plans[0].features);

const faqs = [
  {
    q: 'Can I use my own domain name?',
    a: 'Yes! Professional and Enterprise plans include custom domain support. You point your DNS to our servers and your clients see your domain — no "eventprosuite.com" branding anywhere.',
  },
  {
    q: 'How does the white-label branding work?',
    a: 'You upload your logo, set your colors, and enter your business details. Your branding appears on the public site, client portal, vendor portal, automated emails, and generated contracts — completely replacing our branding.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Absolutely. No long-term contracts. Cancel from your account settings at any time. Your data is exportable for 30 days after cancellation.',
  },
  {
    q: 'How does the automated vendor dispatch work?',
    a: 'When a client adds a vendor service (photographer, bartender, etc.), the system automatically emails your CRM vendors in that category to check availability and rates. When a vendor accepts, they get auto-generated login credentials for the vendor portal.',
  },
  {
    q: 'Is there a setup fee?',
    a: 'No setup fees, ever. Your 14-day free trial includes full access to all features on your chosen plan. No credit card required to start.',
  },
  {
    q: 'What payment methods can my clients use?',
    a: 'Your clients can pay via Stripe (credit/debit cards), PayPal, Venmo, Cash App, and Zelle. You configure which methods to display in your settings.',
  },
];

function FeatureValue({ value }: { value: boolean | string }) {
  if (value === true) return <CheckCircle size={16} className="text-primary mx-auto" />;
  if (value === false) return <span className="text-muted-foreground/40 text-lg mx-auto block text-center">—</span>;
  return <span className="text-xs text-accent font-semibold">{value}</span>;
}

export default function WhiteLabelPricing() {
  const [annual, setAnnual] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const handleSubscribe = async (planName: string) => {
    if (planName === 'Enterprise') return; // Enterprise goes to demo/contact
    setLoadingPlan(planName);
    setCheckoutError(null);
    try {
      const res = await fetch('/api/white-label/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: planName, interval: annual ? 'annual' : 'monthly' }),
      });
      const data = await res.json();
      if (!res.ok || !data.url) {
        setCheckoutError(data.error || 'Unable to start checkout. Please try again.');
        setLoadingPlan(null);
        return;
      }
      window.location.href = data.url;
    } catch (err: any) {
      setCheckoutError('Network error. Please try again.');
      setLoadingPlan(null);
    }
  };

  return (
    <main className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-card/95 backdrop-blur-md border-b neon-border-gold">
        <div className="max-w-screen-xl mx-auto px-6 flex items-center justify-between h-16">
          <Link href="/white-label" className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors text-sm">
            <ArrowLeft size={16} /> Back to Home
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg gold-gradient flex items-center justify-center">
              <Music2 size={13} className="text-black" />
            </div>
            <span className="font-bold text-sm gold-gradient-text">EventPro Suite</span>
          </div>
          <button
            onClick={() => handleSubscribe('Professional')}
            disabled={loadingPlan === 'Professional'}
            className="px-4 py-2 rounded-xl gold-gradient text-black font-semibold text-sm hover:opacity-90 transition-all disabled:opacity-60"
          >
            {loadingPlan === 'Professional' ? <Loader2 size={14} className="animate-spin" /> : 'Start Free Trial'}
          </button>
        </div>
      </div>

      <div className="max-w-screen-xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-14">
          <h1 className="text-5xl font-extrabold gold-gradient-text mb-4">Simple Pricing</h1>
          <p className="text-muted-foreground text-lg mb-8">14-day free trial on all plans. No credit card required.</p>

          {/* Annual/Monthly Toggle */}
          <div className="inline-flex items-center gap-3 p-1 rounded-xl bg-muted border border-border">
            <button
              onClick={() => setAnnual(false)}
              className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${!annual ? 'bg-card text-primary border border-primary/30' : 'text-muted-foreground'}`}
            >
              Monthly
            </button>
            <button
              onClick={() => setAnnual(true)}
              className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${annual ? 'bg-card text-primary border border-primary/30' : 'text-muted-foreground'}`}
            >
              Annual
              <span className="px-2 py-0.5 rounded-full bg-primary/20 text-primary text-[10px] font-bold">SAVE 20%</span>
            </button>
          </div>
        </div>

        {/* Checkout error */}
        {checkoutError && (
          <div className="max-w-xl mx-auto mb-8 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm text-center">
            {checkoutError}
          </div>
        )}

        {/* Plan Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`card-glass rounded-2xl p-8 flex flex-col ${plan.highlight ? 'neon-border-gold glow-gold' : 'border border-border'}`}
            >
              {plan.highlight && (
                <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/20 text-primary text-xs font-bold mb-4 self-start">
                  <Sparkles size={10} /> MOST POPULAR
                </div>
              )}
              <h2 className="text-xl font-bold mb-1">{plan.name}</h2>
              <p className="text-muted-foreground text-sm mb-5">{plan.desc}</p>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-extrabold gold-gradient-text">
                  ${annual ? plan.annualPrice : plan.monthlyPrice}
                </span>
                <span className="text-muted-foreground text-sm">/month</span>
              </div>
              {annual && (
                <p className="text-xs text-accent mb-4 -mt-4">Billed ${plan.annualPrice * 12}/year</p>
              )}
              {plan.name === 'Enterprise' ? (
                <Link
                  href="/white-label/demo"
                  className="w-full text-center py-3 rounded-xl font-semibold transition-all mb-2 border border-primary/40 text-primary hover:bg-primary/10"
                >
                  Contact Sales
                </Link>
              ) : (
                <button
                  onClick={() => handleSubscribe(plan.name)}
                  disabled={loadingPlan === plan.name}
                  className={`w-full text-center py-3 rounded-xl font-semibold transition-all mb-2 flex items-center justify-center gap-2 disabled:opacity-60 ${plan.highlight ? 'gold-gradient text-black hover:opacity-90' : 'border border-primary/40 text-primary hover:bg-primary/10'}`}
                >
                  {loadingPlan === plan.name ? (
                    <><Loader2 size={16} className="animate-spin" /> Redirecting...</>
                  ) : 'Start Free Trial'}
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Feature Comparison Table */}
        <div className="card-glass rounded-2xl overflow-hidden border border-border mb-16">
          <div className="p-6 border-b border-border">
            <h2 className="text-2xl font-bold">Full Feature Comparison</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left px-6 py-4 text-sm font-semibold text-muted-foreground w-1/3">Feature</th>
                  {plans.map(p => (
                    <th key={p.name} className={`px-6 py-4 text-sm font-bold text-center ${p.highlight ? 'text-primary' : 'text-foreground'}`}>
                      {p.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {featureRows.map((row, i) => (
                  <tr key={row} className={`border-b border-border/50 ${i % 2 === 0 ? 'bg-muted/20' : ''}`}>
                    <td className="px-6 py-3 text-sm text-muted-foreground">{row}</td>
                    {plans.map(p => (
                      <td key={p.name} className="px-6 py-3 text-center">
                        <FeatureValue value={p.features[row as keyof typeof p.features]} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ */}
        <div className="max-w-2xl mx-auto mb-16">
          <h2 className="text-3xl font-extrabold gold-gradient-text text-center mb-10">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="card-glass rounded-xl border border-border overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left"
                >
                  <span className="font-semibold text-sm text-foreground">{faq.q}</span>
                  {openFaq === i ? <ChevronUp size={16} className="text-primary flex-shrink-0" /> : <ChevronDown size={16} className="text-muted-foreground flex-shrink-0" />}
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-sm text-muted-foreground leading-relaxed border-t border-border/50 pt-3">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Enterprise CTA */}
        <div className="card-glass rounded-2xl p-10 text-center neon-border-gold">
          <h3 className="text-2xl font-bold mb-2">Need a custom solution?</h3>
          <p className="text-muted-foreground mb-6">Enterprise plans include multi-brand management, API access, and dedicated onboarding support.</p>
          <Link href="/white-label/demo" className="inline-flex items-center gap-2 px-8 py-3 rounded-xl gold-gradient text-black font-bold hover:opacity-90 transition-all">
            Talk to Sales
          </Link>
        </div>
      </div>
    </main>
  );
}
